import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fmtTime } from "@/lib/format";
import { MemberAvatar } from "@/components/Atoms";
import { FeedbackChip } from "@/components/FeedbackChip";
import { useActivityFeedbackEnabled } from "@/hooks/usePrefs";

// ─── Scope hook — shared across Plan tabs ───
//
// Returns `{ scope, setScope, me, scopePersonId }`.
//   scope:           "me" | "family" | <person_id>
//   scopePersonId:   the resolved person id when scope is "me" or a member id;
//                    null when scope === "family".
//
// Default is "me" — every screen is caller-first unless the user opts into
// "family" or an explicit other member. Persisted to localStorage under
// `kinly:scope:{family_id}:{user_id}` so the choice survives reloads.
export function useScope(family, members, currentUserId) {
  const me = (members || []).find((m) => m.user_id === currentUserId) || null;
  const key = family && currentUserId
    ? `kinly:scope:${family.id}:${currentUserId}`
    : null;
  const [scope, setScopeState] = useState("me");
  useEffect(() => {
    if (!key) return;
    try {
      const v = localStorage.getItem(key);
      if (v) setScopeState(v);
    } catch { /* noop */ }
  }, [key]);
  const setScope = (next) => {
    setScopeState(next);
    if (key) {
      try { localStorage.setItem(key, next); } catch { /* noop */ }
    }
  };
  const scopePersonId =
    scope === "family" ? null
    : scope === "me" ? (me?.id || null)
    : scope;
  return { scope, setScope, me, scopePersonId };
}

// ─── Compact 3-state scope chips (Just me / Family / per-member) ───
//
// Used by ChoresView / TodosView / PollsView (and could replace MemberFilter
// on CalendarView once Calendar is rewired off the multi-toggle model).
export function ScopeChips({ scope, onScopeChange, members, me }) {
  if (!members || members.length === 0) return null;
  const others = members.filter((m) => m.id !== me?.id);
  return (
    <div
      data-testid="scope-chips"
      className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 scrollbar-hide"
    >
      {me && (
        <ScopeChip
          testid="scope-chip-me"
          active={scope === "me"}
          member={me}
          label="Just me"
          onClick={() => onScopeChange("me")}
        />
      )}
      <ScopeChip
        testid="scope-chip-family"
        active={scope === "family"}
        label="Family"
        onClick={() => onScopeChange("family")}
      />
      <span className="w-px self-stretch bg-border/60 mx-1 shrink-0" />
      {others.map((m) => (
        <ScopeChip
          key={m.id}
          testid={`scope-chip-${m.id}`}
          active={scope === m.id}
          member={m}
          label={m.display_name?.split(" ")[0]}
          onClick={() => onScopeChange(m.id)}
        />
      ))}
    </div>
  );
}

function ScopeChip({ active, member, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={[
        "shrink-0 inline-flex items-center gap-1.5 h-8 pl-1 pr-3 rounded-full border tap text-[12px] font-medium",
        active
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-card text-foreground",
      ].join(" ")}
    >
      {member ? (
        <MemberAvatar member={member} size={22} ring={false} testid={`${testid}-avatar`} />
      ) : null}
      <span>{label}</span>
    </button>
  );
}

// ─── Compact member multi-picker used inside CreateSheets ───
//
// Renders one chip per member. Tapping a chip toggles its inclusion.
// Pre-selects [me] on mount so the contract "creator owns it unless
// otherwise stated" is visible in the UI itself.
export function MemberMultiPicker({ members, selectedIds, onChange, testid = "member-picker" }) {
  if (!members || members.length === 0) return null;
  const toggle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange([...set]);
  };
  return (
    <div data-testid={testid} className="flex flex-wrap gap-2 mt-1">
      {members.map((m) => {
        const active = (selectedIds || []).includes(m.id);
        return (
          <button
            key={m.id}
            type="button"
            data-testid={`${testid}-${m.id}`}
            onClick={() => toggle(m.id)}
            className={[
              "inline-flex items-center gap-1.5 h-9 pl-1 pr-3 rounded-full border tap text-[12px] font-medium",
              active
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-card text-foreground",
            ].join(" ")}
          >
            <MemberAvatar member={m} size={26} ring={false} />
            <span>{m.display_name?.split(" ")[0]}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Generic field wrapper used by all CreateSheets ───
export function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
        {label}
      </span>
      {children}
    </label>
  );
}

// ─── Empty-state hint, also re-exported as EmptyState for backwards-compat ───
export function EmptyHint({ eyebrow, headline, body }) {
  return (
    <div className="text-center py-10">
      {eyebrow && (
        <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
          {eyebrow}
        </p>
      )}
      <h2 className="font-display text-[22px] tracking-tight font-medium text-foreground">
        {headline}
      </h2>
      <p className="text-sm text-muted-foreground mt-3 max-w-xs mx-auto leading-relaxed">
        {body}
      </p>
    </div>
  );
}
export { EmptyHint as EmptyState };

// ─── Member filter chips on Calendar (All / Just me / per-member toggles) ───
export function MemberFilter({ members, hidden, onToggle, onSetHidden }) {
  const { user } = useAuth();
  if (!members || members.length === 0) return null;
  const me = members.find((m) => m.user_id === user?.id);
  const allShown = hidden.size === 0;
  const justMe = me && hidden.size === members.length - 1 && !hidden.has(me.id);

  return (
    <div data-testid="member-filter" className="flex gap-2 overflow-x-auto scrollbar-hide items-center">
      <button
        data-testid="member-chip-all"
        onClick={() => onSetHidden([])}
        className={[
          "shrink-0 inline-flex items-center h-7 px-3 rounded-full border tap text-[12px] font-medium",
          allShown ? "border-foreground bg-foreground text-background" : "border-border bg-card text-foreground",
        ].join(" ")}
      >
        All
      </button>
      {me && (
        <button
          data-testid="member-chip-just-me"
          onClick={() => onSetHidden(members.filter((m) => m.id !== me.id).map((m) => m.id))}
          className={[
            "shrink-0 inline-flex items-center gap-1.5 h-7 pl-1 pr-3 rounded-full border tap text-[12px] font-medium",
            justMe ? "border-foreground bg-foreground text-background" : "border-border bg-card text-foreground",
          ].join(" ")}
        >
          <MemberAvatar member={me} size={22} ring={false} />
          Just me
        </button>
      )}
      <span className="w-px self-stretch bg-border/60 mx-1 shrink-0" />
      {members.map((m) => {
        const isHidden = hidden.has(m.id);
        return (
          <button
            key={m.id}
            data-testid={`member-chip-${m.id}`}
            onClick={() => onToggle(m.id)}
            className={[
              "shrink-0 inline-flex items-center gap-1.5 h-7 pl-1 pr-3 rounded-full border tap text-[12px] font-medium",
              isHidden
                ? "border-border bg-card text-muted-foreground"
                : "border-foreground/15 bg-card text-foreground",
            ].join(" ")}
          >
            <span className={isHidden ? "opacity-40 grayscale" : ""}>
              <MemberAvatar member={m} size={22} ring={false} />
            </span>
            <span className={isHidden ? "line-through" : ""}>{m.display_name?.split(" ")[0]}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Single calendar view tab (agenda / day / week / month) ───
export function ViewTab({ id, current, setCurrent, locked, icon: Icon, label }) {
  const active = current === id;
  return (
    <button
      data-testid={`view-tab-${id}`}
      onClick={() => setCurrent(id)}
      className={[
        "flex-1 h-9 rounded-full tap inline-flex items-center justify-center gap-1.5 font-medium",
        active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground",
      ].join(" ")}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {locked && <Lock className="w-3 h-3 ml-0.5" />}
    </button>
  );
}

// ─── Single row in an agenda/week/day list. Kind-aware: works for events,
// chores, todos, medication doses, and polls. Shows a colored kind-dot,
// time, title, optional location, and a Kinly badge. Strikethrough when
// the item is completed (used in Agenda only — Day/Week/Month filter
// completed out higher up).
import { isCompleted, styleOf } from "@/lib/itemChip";
export function EventRow({ event, members }) {
  const navigate = useNavigate();
  const feedbackEnabled = useActivityFeedbackEnabled();
  const s = styleOf(event);
  const done = isCompleted(event);
  const time = event.all_day
    ? "all-day"
    : event.type === "todo" && !event.due_at && !event.start_at
      ? ""
      : fmtTime(event.start_at || event.due_at || event.closes_at);
  // Route per kind so detail sheets open correctly. Centralised in
  // calendarRoute.js so the routing logic stays in one place; medication_dose
  // is the only exception — there's no per-dose detail page (intentional).
  const onClick = () => {
    if (event.type === "medication_dose") return navigate("/today");
    import("@/lib/calendarRoute").then(({ calendarItemRoute }) => {
      navigate(calendarItemRoute(event));
    });
  };
  // Members associated with this row — events use participant_ids,
  // chores/todos use assignee_person_ids.
  const ids = event.participant_ids || event.assignee_person_ids || (event.owner_person_id ? [event.owner_person_id] : []);
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        data-testid={`event-row-${event.id}`}
        onClick={onClick}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
        className={`w-full flex items-center gap-3 py-2.5 tap text-left cursor-pointer ${done ? "opacity-55" : ""}`}
      >
        <span
          className="inline-block w-1.5 h-12 rounded-full shrink-0"
          style={{ backgroundColor: s.dot }}
          aria-hidden
        />
        <ParticipantDots ids={ids} members={members} />
        <span className="font-mono text-xs tabular-nums text-muted-foreground w-12">{time}</span>
        <div className="flex-1 min-w-0">
          <p className={`text-[15px] truncate ${done ? "line-through" : ""}`}>{event.title}</p>
          {event.location_name && (
            <p className="text-xs text-muted-foreground truncate">{event.location_name}</p>
          )}
        </div>
        {event.suggestion_source === "kinly" && (
          <span className="text-[10px] uppercase tracking-wider text-primary font-medium">
            Kinly
          </span>
        )}
        <FeedbackChip event={event} enabled={feedbackEnabled} />
        <ChevronRight className="w-4 h-4 text-muted-foreground" />
      </div>
    </li>
  );
}

// ─── Stacked participant avatars used on event rows ───
export function ParticipantDots({ ids, members, max = 4 }) {
  const visible = (ids || [])
    .map((id) => members.find((m) => m.id === id))
    .filter(Boolean);
  if (visible.length === 0) {
    return (
      <MemberAvatar
        name="?"
        color="#888"
        size={28}
        ring={false}
        testid="participant-unknown"
      />
    );
  }
  if (visible.length === 1) {
    const m = visible[0];
    return (
      <span title={m.display_name} className="shrink-0">
        <MemberAvatar member={m} size={28} ring={false} />
      </span>
    );
  }
  return (
    <div className="flex -space-x-2 shrink-0">
      {visible.slice(0, max).map((m) => (
        <span
          key={m.id}
          title={m.display_name}
          className="ring-2 ring-card rounded-full"
        >
          <MemberAvatar member={m} size={28} ring={false} />
        </span>
      ))}
      {visible.length > max && (
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-secondary text-foreground text-[10px] font-semibold font-display ring-2 ring-card">
          +{visible.length - max}
        </span>
      )}
    </div>
  );
}
